import { useCallback, useEffect, useMemo, useState } from 'react'

const cx_flatten = (from, to = []) => {
  if (typeof from === 'string' || from instanceof String)
    to.push(from)
  else if (from && typeof from === 'object')
    if (typeof from[Symbol.iterator] === 'function')
      for (const item of from)
        cx_flatten(item, to)
    else
      for (const key in from)
        if (from[key])
          to.push(key)
  return to
}

export const cx = (...args) => cx_flatten(args).join(' ')

export const log = (arg, ...args) => {
  if (arg instanceof Error)
    console.error(arg, ...args)
  else
    console.log(arg, ...args)
  return arg
}
window.log = log

export const logAndThrow = (err, ...args) => {
  console.error(err, ...args)
  if (err instanceof Error)
    throw err
  else
    throw new Error(String(err))
}
window.logAndThrow = logAndThrow

export const rethrow = msg => cause => {
  throw new Error(msg, { cause })
}
window.logAndThrow = logAndThrow

export const call = f => f?.()
export const ignore = () => undefined
export const identity = v => v

export const useLocalStorageState = (item, initalValue = null, initialize = false) => {
  const [value, _setValue] = useState(() => {
    let value = localStorage.getItem(item)
    value = value ? JSON.parse(value) : undefined
    if (value === undefined) {
      value = typeof initalValue === 'function' ? initalValue() : initalValue
      if (initialize && value !== undefined)
        localStorage.setItem(item, JSON.stringify(value))
    }
    return value
  })
  const setValue = useCallback(value => {
    _setValue(prevValue => {
      if (typeof value === 'function') value = value(prevValue)
      if (value === undefined)
        localStorage.removeItem(item)
      else
        localStorage.setItem(item, JSON.stringify(value))
      return value
    })
  }, [])
  return [value, setValue]
}

export const useToggle = ([value, set]) => {
  return [value, useCallback(() => set(v => !v), []), set]
}

const targetOrValue = event => event?.target
  ? (event.target.type === 'checkbox'
    ? event.target.checked
    : event.target.value
  ) : event

export const useInputState = (initialState = '') => {
  const [value, setValue] = useState(initialState)
  const setOnChange = useCallback(event => setValue(targetOrValue(event)), [])
  return [value, setOnChange, setValue]
}

export const useCheckboxState = (initialState = false) => {
  const [value, setValue] = useState(initialState)
  const setOnChange = useCallback(event => setValue(targetOrValue(event)), [])
  return [value, setOnChange]
}

export const useInputStateVerbose = ([value, setValue]) => {
  const setOnChange = useCallback(event => setValue(targetOrValue(event)), [])
  return [value, setOnChange]
}

export const useCheckboxStateVerbose = ([value, setValue]) => {
  const setOnChange = useCallback(event => setValue(targetOrValue(event)), [])
  return [value, setOnChange]
}

export const subscribe = (emitter, event, func) => {
  (emitter.on || emitter.addEventListener)(event, func)
  return () => (emitter.off || emitter.removeEventListener)(event, func)
}

export const subscribeMany = (
  emitter,
  event, func,
  ...morelisteners
) => {
  if (!morelisteners.length)
    return subscribe(emitter, event, func)
  const unsubbers = [subscribe(emitter, event, func)]
  for (let i = 0; morelisteners[i + 1]; i += 2)
    unsubbers.push(subscribe(emitter, morelisteners[i], morelisteners[i + 1]))
  return () => unsubbers.forEach(call)
}

export const useSubscribe = (emitter, event, func, deps = []) => {
  useEffect(() => subscribe(emitter, event, func), deps)
}

export const sleep = ms => new Promise(r => setTimeout(r, ms))

import { atom, useAtom } from 'jotai'

const localConfigAtoms = {}

export const useLocalConfig = (name, initalValue = null, initialize = false) => {
  const [storageValue, setStorageValue] = useLocalStorageState(name, initalValue, initialize)
  if (!localConfigAtoms[name])
    localConfigAtoms[name] = atom(storageValue)
  const [atomValue, setAtomValue] = useAtom(localConfigAtoms[name])
  const setValue = value => {
    setStorageValue(value)
    setAtomValue(value)
  }
  return [atomValue, setValue]
}

export const useRefresh = () => {
  const [val, set] = useState(0)
  const refresh = useCallback(() => set(v => v + 1), [])
  return [val, refresh]
}

export const useOnline = (time, checkRate = 1000) => {
  const [refreshVal, refresh] = useRefresh()
  useFocusEffect(useCallback(() => interval(refresh, checkRate), []))
  return useMemo(() => (
    time ? Date.now() - new Date(time).getTime() < checkRate * 2 : false
  ), [time, checkRate, refreshVal])
}

export const interval = (fn, ms, immediate = false) => {
  if (immediate) fn()
  const id = setInterval(fn, ms)
  return () => clearInterval(id)
}

export const timeout = (fn, ms) => {
  const id = setTimeout(fn, ms)
  return () => clearTimeout(id)
}

export const immediate = (fn) => {
  const id = setTimeout(fn)
  return () => clearTimeout(id)
}

export const byKey = (key, reverse) => (a, b) => a[key] === b[key] ? 0 : (a[key] > b[key] ? (reverse ? -1 : 1) : (reverse ? 1 : -1))
export const keyMatch = (key, value) => v => v[key] === value

export const Null = () => null

export const ruEnumerate = (num, ...words) => {
  if (num > 100) num = num % 100
  if (num <= 20 && num >= 10) return words[2]
  if (num > 20) num = num % 10
  return num === 1 ? words[0] : num > 1 && num < 5 ? words[1] : words[2]
}

export const ruDateTimeSec = date => !date ? '' : (date instanceof Date ? date : new Date(date)).toLocaleString('ru-RU').replace(',', '')
export const ruDateTime = date => ruDateTimeSec(date).replace(/...$/, '')

export const ellipsis = (str, maxLen) => {
  if (str.length < maxLen + 3)
    return str
  else
    return str.slice(0, maxLen) + '...'
}

export const groupByKey = (values, key) => {
  const acc = {}
  if (Array.isArray(values)) {
    for (const value of values) {
      if (!(value[key] in acc))
        acc[value[key]] = []
      acc[value[key]].push(value)
    }
  } else {
    for (const id in values) {
      const value = values[id]
      if (!(value[key] in acc))
        acc[value[key]] = {}
      acc[value[key]][id] = value
    }
  }
  return acc
}

export const objectFilter = (obj, filter) => Object.fromEntries(Object.entries(obj).filter(([k, v]) => filter(v, k)))
export const objectMap = (obj, map) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, map(v, k)]))
export const objectRemap = (obj, remap) => Object.fromEntries(Object.entries(obj).map(([k, v]) => remap(v, k)))
export const arrayToObject = (arr, key) => Object.fromEntries(arr.map((v, i) => [key ? v[key] : i, v]))

const namedAtoms = {}

export const useNamedAtom = (name, initialValue) => {
  let namedAtom = namedAtoms[name]
  if (!namedAtom) {
    namedAtom = atom(initialValue)
    namedAtoms[name] = namedAtom
  }
  return useAtom(namedAtom)
}

const intOrZero = numString => {
  const parsed = parseInt(numString)
  return isNaN(parsed) ? 0 : parsed
}

export const versionParse = version => {
  const parts = String(version).split('.', 3).map(v => v.split('-', 2))
  const major = intOrZero(parts?.[0]?.[0])
  const minor = intOrZero(parts?.[1]?.[0])
  const patch = intOrZero(parts?.[2]?.[0])
  return { major, minor, patch }
}

export const versionCompare = (aStr, bStr) => {
  if (aStr === bStr) return 0
  const a = versionParse(aStr)
  const b = versionParse(bStr)
  if (a.major > b.major) return 1
  if (a.major < b.major) return -1
  if (a.minor > b.minor) return 1
  if (a.minor < b.minor) return -1
  if (a.patch > b.patch) return 1
  if (a.patch < b.patch) return -1
  return 0
}

export const versionIsUpgrade = (local, incoming) => {
  return versionCompare(incoming, local) > 0
}

export const expandToUUID = text => {
  const uuidStr = text.toLowerCase()
  if (uuidStr.length === 32) {
    return `${uuidStr.slice(0, 8)}-${uuidStr.slice(8, 12)}-${uuidStr.slice(12, 16)}-${uuidStr.slice(16, 20)}-${uuidStr.slice(20)}`
  } else if (uuidStr.length === 36) {
    return uuidStr
  } else {
    throw new Error(`uuid bad: ${text}`)
  }
}

import { phone as parsePhone } from 'phone'

export const normalizePhone = (phone) => {
  const parsedGlobal = parsePhone(phone)
  if (parsedGlobal.isValid)
    return parsedGlobal.phoneNumber

  // handle common ru variants
  const onlyDigits = phone.replace(/[^0-9]/g, '')
  if (onlyDigits.length === 11 && onlyDigits[0] === '8' || onlyDigits[0] === '7')
    return parsePhone(onlyDigits.replace(/^\d/, '+7')).phoneNumber || ''
}